




























import { computed, defineComponent, getCurrentInstance, ref, toRef, watch } from "@vue/composition-api";
import {
  ContentFeed,
  ContentFeedSearchResult,
  ContentFeedTypeEnum,
} from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import useAuthUser from "@/hooks/useAuthUser";
import usePager from "@/hooks/usePager";
import providers from "@/lib/api";
import BasePage from "@/components/BasePage";
import ContentFeedView from "@/components/ContentFeedView";
import PageTitle from "@/components/PageTitle";

export default defineComponent({
  name: "PurchasesGalleryFeed",
  components: { BasePage, PageTitle, ContentFeedView },
  props: {
    username: { type: String, required: true },
    onFeed: { type: Boolean, default: false },
  },
  setup(props) {
    const vm = getCurrentInstance();
    const { authUserStrict } = useAuthUser();
    const loading = ref<boolean>(false);
    const feedId = ref<string | null>(null);
    const owner = ref<UserPublicData | null>(null);

    const { getFirstPage, enableScroll, getNextPage, items, data } = usePager(page => {
      if (!owner.value) {
        throw new Error("Owner required");
      }
      return providers.PurchasedFileProvider.search({
        data: {
          userId: authUserStrict.value.id,
          ownerId: owner.value.id,
          page,
          size: 20,
          sort: "TIME,DESC",
        },
      });
    });

    const contentFeedSearchResult = computed<ContentFeedSearchResult>(() => {
      if (!owner.value) {
        return {
          page: 0,
          size: 0,
          totalRecords: 0,
          totalPages: 0,
          hasNextPage: false,
          hasPrevPage: false,
          result: [],
        };
      }
      const user = owner.value;
      const result: ContentFeed[] = items.value.map(item => ({
        purchased: true,
        type: ContentFeedTypeEnum.MEDIA,
        user,
        files: [
          {
            id: item.id,
            mediaType: item.metadata.contentType,
            name: item.metadata.filename,
            publicAccess: true,
            setId: "",
            size: 0,
            width: parseInt(item.metadata.width),
            height: parseInt(item.metadata.height),
            sort: 0,
            title: "",
            uploadTime: item.time,
            urlPart: item.urlPart,
            uuid: "",
            processingStatus: "OK",
            duration: parseInt(item.metadata.duration),
          },
        ],
        likes: {
          subject: {
            subjectId: item.subject.subjectId,
            subjectType: item.subject.subjectType,
          },
          count: 0,
          liked: false,
        },
        subscriptionLevelId: "",
        pricePackageId: "",
        details: {},
        id: item.id,
        userId: item.ownerId,
        createTime: item.time,
        modifyTime: item.time,
        publishTime: null,
        scheduleTime: null,
        processingStatus: "OK",
        size: 0,
      }));
      return {
        page: data.value?.page ?? 0,
        size: data.value?.size ?? 0,
        totalRecords: data.value?.totalRecords ?? 0,
        totalPages: data.value?.totalPages ?? 0,
        hasNextPage: data.value?.hasNextPage ?? false,
        hasPrevPage: data.value?.hasPrevPage ?? false,
        result,
      };
    });
    const totalRecords = computed(() => contentFeedSearchResult.value.totalRecords);

    const getPageData = async () => {
      try {
        loading.value = true;
        const response = await providers.UserPublicDataProvider.search({
          data: {
            usernames: [props.username],
          },
        });
        owner.value = response.result[0];
        await getFirstPage();
      } finally {
        loading.value = false;
      }
    };

    const openFeed = (clickedFeedId: string) => {
      feedId.value = clickedFeedId;
      vm?.$router.push({ name: "purchasesFeedList" });
    };

    watch(
      toRef(props, "onFeed"),
      (status: boolean) => {
        if (!status && feedId.value) {
          feedId.value = null;
        }
      },
      { immediate: true }
    );

    getPageData();

    return { owner, enableScroll, totalRecords, loading, getNextPage, feedId, openFeed, contentFeedSearchResult };
  },
});
